import { DocumentIcon } from '@heroicons/react/24/outline';

import { getFileExtension, isPdf } from 'src/shared/utils/fileTypeUtils';

import { cn } from 'src/shared/components/ui';
import { Typography } from 'src/shared/components/ui';

/**
 * Props for FilePreview when using a native File object
 */
interface FilePreviewWithFileProps {
  file: File;
  previewUrl: string;
  className?: string;
}

/**
 * Props for FilePreview when using uploaded file data (URL-based)
 */
interface FilePreviewWithUrlProps {
  url: string;
  name: string;
  contentType: string;
  className?: string;
}

type FilePreviewProps = FilePreviewWithFileProps | FilePreviewWithUrlProps;

/**
 * Type guard to check if props are URL-based
 */
function isUrlBasedProps(
  props: FilePreviewProps
): props is FilePreviewWithUrlProps {
  return 'url' in props && 'contentType' in props;
}

/**
 * Component for rendering non-image file previews (PDF, DOC, DOCX, etc.)
 * - PDFs are rendered in an iframe for embedded viewing
 * - Other files show a styled fallback with file info
 *
 * Supports both native File objects and URL-based uploaded file data.
 */
export function FilePreview(props: FilePreviewProps): React.ReactElement {
  const { className } = props;

  // Extract file info based on props type
  const fileName = isUrlBasedProps(props) ? props.name : props.file.name;
  const contentType = isUrlBasedProps(props)
    ? props.contentType
    : props.file.type;
  const previewUrl = isUrlBasedProps(props) ? props.url : props.previewUrl;

  const fileExtension = getFileExtension(fileName);

  // Render PDF in an iframe for embedded viewing
  if (isPdf(contentType, fileName)) {
    return (
      <iframe
        src={previewUrl}
        title={fileName}
        className={cn(
          'h-full w-full rounded-2xl border border-gray-4 bg-white',
          className
        )}
      />
    );
  }

  // Fallback for other file types (DOC, DOCX, etc.)
  return (
    <div
      className={cn(
        'h-full w-full bg-gray-2 rounded-2xl border border-gray-4 flex flex-col items-center justify-center gap-4 p-6',
        className
      )}
    >
      <div className="bg-gray-3 size-20 flex items-center justify-center rounded-full">
        <DocumentIcon className="size-10 text-gray-600" />
      </div>
      <div className="flex flex-col items-center gap-1 text-center max-w-full">
        <Typography
          variant="body-1"
          weight="medium"
          className="truncate max-w-full px-4"
        >
          {fileName}
        </Typography>
        <Typography variant="body-2" className="text-secondary-text">
          {fileExtension} Document
        </Typography>
      </div>
    </div>
  );
}
